import { useState } from "react";
import { toast } from "sonner";
import {
  Menu, X, ArrowRight, ShieldCheck, Users, Database, Mail, CalendarDays,
  MapPinned, HandCoins, Globe, Building2, Lock, KeyRound, ScrollText,
  Layers, Sparkles, FileSpreadsheet, MailWarning, Boxes, CircleUserRound,
} from "lucide-react";
import { LogoLockup, BrandMark } from "@/components/Logo";
import { Button, Badge, SectionLabel } from "@/components/redvolt/kit";

const NAV = [
  { label: "Product", href: "#capabilities" },
  { label: "Solutions", href: "#built-for" },
  { label: "Security", href: "#security" },
  { label: "Pricing", href: "#pricing" },
];

const CAPABILITIES = [
  { icon: Users, name: "Voter & Contact CRM", desc: "One organized record of members, volunteers, donors, and voters." },
  { icon: Database, name: "Voter File & Segmentation", desc: "Target outreach by precinct, district, and turnout history." },
  { icon: Mail, name: "Email & Communications", desc: "Reach the right people with the right message at the right time." },
  { icon: HandCoins, name: "Donations & Fundraising", desc: "Track giving and compliance details in one place." },
  { icon: CalendarDays, name: "Events & Meetings", desc: "Plan meetings, rallies, and fundraisers with check-ins." },
  { icon: MapPinned, name: "Volunteers & Walk Lists", desc: "Coordinate canvassing, phone banks, and shifts." },
  { icon: Globe, name: "Websites & Landing Pages", desc: "Launch a branded presence and event sign-up pages." },
  { icon: Building2, name: "Organizational Management", desc: "Roles, permissions, and accountable, auditable access." },
];

const PROBLEMS = [
  { icon: FileSpreadsheet, text: "Voter and member data scattered across spreadsheets with no single source of truth." },
  { icon: MailWarning, text: "Outdated email lists and disconnected tools that don't talk to each other." },
  { icon: Boxes, text: "Fundraising, events, and websites managed in separate places." },
  { icon: CircleUserRound, text: "Institutional knowledge lost every time leadership rotates." },
];

const AUDIENCE = [
  "County Committees", "Area Organizations", "Township & Local Committees",
  "Republican Clubs", "Legislative & Local Campaigns", "Candidates",
];

const SECURITY = [
  { icon: Lock, title: "Secure sign-in", desc: "Modern authentication with multi-factor protection for every account." },
  { icon: Layers, title: "Organizational separation", desc: "Each organization's information is kept separate and private." },
  { icon: KeyRound, title: "Permission-based access", desc: "People only see what their role allows — nothing more." },
  { icon: ScrollText, title: "Auditable activity", desc: "Important actions are recorded so leadership can stay accountable." },
];

const comingSoon = (name) =>
  toast(`${name} is coming soon`, { description: "This part of RedVolt isn't available yet." });

const Header = ({ onSignIn, onGetStarted }) => {
  const [open, setOpen] = useState(false);
  const onNav = (item, e) => {
    if (item.href === "#pricing") {
      e.preventDefault();
      comingSoon("Pricing");
    }
    setOpen(false);
  };
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <a href="#top" className="flex items-center" aria-label="RedVolt home">
          <LogoLockup />
        </a>
        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          {NAV.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => onNav(item, e)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-2 md:flex">
          <Button variant="ghost" size="sm" onClick={onSignIn} data-testid="header-sign-in">Sign In</Button>
          <Button size="sm" onClick={onGetStarted} data-testid="header-get-started">Get Started</Button>
        </div>
        <button
          className="grid h-10 w-10 place-items-center rounded-md text-slate-700 hover:bg-slate-100 md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {NAV.map((item) => (
              <a key={item.label} href={item.href} onClick={(e) => onNav(item, e)}
                 className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
                {item.label}
              </a>
            ))}
          </nav>
          <div className="mt-3 flex flex-col gap-2">
            <Button variant="outline" onClick={onSignIn}>Sign In</Button>
            <Button onClick={onGetStarted}>Get Started</Button>
          </div>
        </div>
      )}
    </header>
  );
};

// Conceptual, clearly-labelled preview. No fabricated data/metrics.
const ProductPreview = () => (
  <div className="relative" data-testid="hero-preview">
    <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-accent to-slate-100 blur-2xl opacity-70" />
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 border border-slate-200">
          <Sparkles className="h-3 w-3 text-primary" /> Conceptual preview
        </span>
      </div>
      <div className="flex">
        <div className="hidden w-40 flex-col gap-1 border-r border-slate-200 bg-white p-3 sm:flex">
          <div className="mb-1 flex items-center gap-2">
            <BrandMark size={18} />
            <span className="font-display text-sm font-bold text-slate-900">RedVolt</span>
          </div>
          {["Dashboard", "People", "Communications", "Events", "Fundraising"].map((n, i) => (
            <div key={n} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${i === 0 ? "bg-accent font-semibold text-accent-foreground border-l-2 border-primary" : "text-slate-500"}`}>
              <span className={`h-2 w-2 rounded-sm ${i === 0 ? "bg-primary" : "bg-slate-300"}`} />
              {n}
            </div>
          ))}
        </div>
        <div className="flex-1 space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Active Organization</p>
              <p className="font-display text-sm font-bold text-slate-900">Chester County GOP</p>
            </div>
            <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold text-accent-foreground">Active</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["Organization", "Your Access", "Account protected", "Recent items"].map((c) => (
              <div key={c} className="rounded-lg border border-slate-200 bg-white p-2.5">
                <div className="h-1.5 w-10 rounded bg-slate-200" />
                <p className="mt-2 text-[11px] font-semibold text-slate-700">{c}</p>
                <div className="mt-2 space-y-1">
                  <div className="h-1.5 w-full rounded bg-slate-100" />
                  <div className="h-1.5 w-2/3 rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Hero = ({ onGetStarted }) => (
  <section id="top" className="relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_80%_-10%,#ffe3ea_0%,transparent_60%)]" />
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Political technology, done right
        </span>
        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
          Run Your Republican Organization <span className="text-primary">Smarter.</span>
        </h1>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
          One secure platform for voters, volunteers, communications, events, fundraising, and the work that keeps your organization moving.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button size="lg" onClick={onGetStarted} data-testid="hero-get-started">
            Get Started <ArrowRight className="h-4 w-4" />
          </Button>
          <a href="#how">
            <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="hero-see-how">
              See How RedVolt Works
            </Button>
          </a>
        </div>
      </div>
      <ProductPreview />
    </div>
  </section>
);

const Problem = () => (
  <section className="border-y border-slate-200 bg-white">
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
      <div className="max-w-2xl">
        <SectionLabel>The problem</SectionLabel>
        <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          Political organizations run on disconnected tools.
        </h2>
        <p className="mt-3 text-slate-600">
          Most committees and campaigns stitch together spreadsheets, email lists, voter files, and shared documents. It's slow, error-prone, and easy to lose track of what matters.
        </p>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {PROBLEMS.map((p) => (
          <div key={p.text} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-white text-slate-500 border border-slate-200">
              <p.icon className="h-4 w-4" />
            </span>
            <p className="text-sm leading-relaxed text-slate-700">{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const OnePlace = () => (
  <section id="how" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
    <div className="mx-auto max-w-2xl text-center">
      <SectionLabel className="text-center">One place to run the organization</SectionLabel>
      <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
        RedVolt brings your operations together.
      </h2>
      <p className="mt-4 text-lg text-slate-600">
        Instead of a patchwork of tools, RedVolt is a single, secure operating system for your people, communications, events, and fundraising — built for how political organizations actually work.
      </p>
    </div>
  </section>
);

const Capabilities = () => (
  <section id="capabilities" className="border-y border-slate-200 bg-white">
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div className="max-w-2xl">
          <SectionLabel>Platform vision</SectionLabel>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Everything an organization needs, on the roadmap.
          </h2>
          <p className="mt-3 text-slate-600">
            RedVolt is being built module by module. The capabilities below describe where the platform is headed.
          </p>
        </div>
        <Badge variant="vision" data-testid="vision-badge">
          <Sparkles className="h-3 w-3" /> Product direction — not yet available
        </Badge>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CAPABILITIES.map((c) => (
          <div key={c.name} className="group rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-accent text-primary">
              <c.icon className="h-5 w-5" />
            </span>
            <p className="mt-4 text-sm font-semibold text-slate-900">{c.name}</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-500">{c.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const BuiltFor = () => (
  <section id="built-for" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
    <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
      <div>
        <SectionLabel>Built for Republican organizations</SectionLabel>
        <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          From local committees to campaigns.
        </h2>
        <p className="mt-3 text-slate-600">
          RedVolt is designed to grow with every level of Republican leadership — not just one kind of organization.
        </p>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {AUDIENCE.map((a) => (
          <span key={a} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {a}
          </span>
        ))}
      </div>
    </div>
  </section>
);

const Security = () => (
  <section id="security" className="border-y border-slate-200 bg-slate-900">
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-200">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Security & trust
        </span>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Security built in from the foundation.
        </h2>
        <p className="mt-3 text-slate-300">
          RedVolt protects sensitive organizational information with modern security — so your team can focus on the work, not the tools.
        </p>
      </div>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SECURITY.map((s) => (
          <div key={s.title} className="rounded-lg border border-white/10 bg-white/5 p-5">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/15 text-primary">
              <s.icon className="h-5 w-5" />
            </span>
            <p className="mt-4 text-sm font-semibold text-white">{s.title}</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-400">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FinalCTA = ({ onGetStarted }) => (
  <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center shadow-sm sm:px-12">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(50%_60%_at_50%_0%,#ffe3ea_0%,transparent_70%)]" />
      <h2 className="mx-auto max-w-2xl font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
        Ready to run your organization smarter?
      </h2>
      <p className="mx-auto mt-3 max-w-xl text-slate-600">
        Create your RedVolt account and set up your organization in minutes.
      </p>
      <div className="mt-8 flex justify-center">
        <Button size="lg" onClick={onGetStarted} data-testid="cta-get-started">
          Get Started <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-slate-200 bg-white">
    <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6">
      <LogoLockup markSize={24} wordClassName="text-lg" />
      <p className="text-sm text-slate-500">© {new Date().getFullYear()} RedVolt. All rights reserved.</p>
    </div>
  </footer>
);

export default function PublicSite({ onSignIn }) {
  // Sign In and Get Started both route to the secure WorkOS hosted flow.
  const go = onSignIn;
  return (
    <div className="min-h-screen bg-background text-slate-900">
      <Header onSignIn={go} onGetStarted={go} />
      <main>
        <Hero onGetStarted={go} />
        <Problem />
        <OnePlace />
        <Capabilities />
        <BuiltFor />
        <Security />
        <FinalCTA onGetStarted={go} />
      </main>
      <Footer />
    </div>
  );
}
